// Bee Match — a bee-themed match-3 (Bejeweled-style) engine.
//
// Swap two adjacent pieces to line up 3+ of a kind. Matches clear, pieces fall,
// and new ones drop in — chaining into cascades. Special "power" pieces are
// created by bigger matches:
//   • a match of 4  → a ROW blaster (clears its whole row when triggered)
//   • a match of 5+ → a HIVE bomb   (clears the entire board when triggered)
// A special triggers when it gets cleared (matched into a group) or swapped.
//
// This module is pure game logic (no React). The UI renders `board`, a 2-D array
// of `{ t, s }` cells: `t` is the piece type (0..types-1), `s` is the special
// flag (SPECIAL.NONE | ROW | BOMB).

export const ROWS = 8;
export const COLS = 8;
export const MAX_TYPES = 7;
// ROW/COL are line blasters (clear their row / column); CROSS clears BOTH its
// row and column; BOMB is a colour bomb (clears every tile of a colour).
// A 4-in-line forges a line blaster; an L/T shape (a row run + a column run that
// intersect) forges a CROSS; a 5-in-line forges a colour bomb.
export const SPECIAL = { NONE: 0, ROW: 1, COL: 2, BOMB: 3, CROSS: 4 };

// 5000 to clear level 1; each level needs 10% more than the previous.
export const thresholdForLevel = (level) =>
  Math.round((5000 * Math.pow(1.1, Math.max(1, level | 0) - 1)) / 10) * 10;
export const timeForLevel = () => 60; // seconds (timed mode)
export const typesForLevel = (level) =>
  Math.min(MAX_TYPES, 5 + Math.floor((Math.max(1, level | 0) - 1) / 12)); // 5 → 7

const key = (r, c) => r * COLS + c;
export const areAdjacent = (a, b) =>
  (a.r === b.r && Math.abs(a.c - b.c) === 1) || (a.c === b.c && Math.abs(a.r - b.r) === 1);
export const cloneBoard = (board) => board.map((row) => row.map((cell) => ({ ...cell })));

function randType(rng, types, avoid) {
  let t;
  let guard = 0;
  do {
    t = Math.floor(rng() * types);
  } while (avoid && avoid.has(t) && guard++ < 20);
  return t;
}

// A fresh board with no pre-existing matches and at least one available move.
// `keep` pins special pieces (carried over from a completed level) to their
// positions; normal tiles fill in around them.
export function createBoard(level = 1, rng = Math.random, keep = []) {
  const types = typesForLevel(level);
  const fixed = new Map();
  for (const sp of keep) {
    if (sp && sp.s && sp.r >= 0 && sp.r < ROWS && sp.c >= 0 && sp.c < COLS) {
      fixed.set(sp.r * COLS + sp.c, { t: Math.min(Math.max(0, sp.t | 0), types - 1), s: sp.s });
    }
  }
  let board;
  let tries = 0;
  do {
    board = [];
    for (let r = 0; r < ROWS; r++) {
      const row = [];
      for (let c = 0; c < COLS; c++) {
        const f = fixed.get(r * COLS + c);
        if (f) {
          row.push({ t: f.t, s: f.s });
          continue;
        }
        const avoid = new Set();
        if (c >= 2 && row[c - 1].t === row[c - 2].t) avoid.add(row[c - 1].t);
        if (r >= 2 && board[r - 1][c].t === board[r - 2][c].t) avoid.add(board[r - 1][c].t);
        row.push({ t: randType(rng, types, avoid), s: SPECIAL.NONE });
      }
      board.push(row);
    }
    tries++;
  } while ((findMatches(board).length > 0 || !hasMove(board, types)) && tries < 60);
  return board;
}

// All horizontal + vertical runs of length ≥ 3 (matched on piece type `t`).
export function findMatches(board) {
  const runs = [];
  for (let r = 0; r < ROWS; r++) {
    let start = 0;
    for (let c = 1; c <= COLS; c++) {
      const same = c < COLS && board[r][c] && board[r][c - 1] && board[r][c].t === board[r][c - 1].t;
      if (!same) {
        if (c - start >= 3) {
          const cells = [];
          for (let k = start; k < c; k++) cells.push({ r, c: k });
          runs.push({ cells, len: c - start, dir: "h" });
        }
        start = c;
      }
    }
  }
  for (let c = 0; c < COLS; c++) {
    let start = 0;
    for (let r = 1; r <= ROWS; r++) {
      const same = r < ROWS && board[r][c] && board[r - 1][c] && board[r][c].t === board[r - 1][c].t;
      if (!same) {
        if (r - start >= 3) {
          const cells = [];
          for (let k = start; k < r; k++) cells.push({ r: k, c });
          runs.push({ cells, len: r - start, dir: "v" });
        }
        start = r;
      }
    }
  }
  return runs;
}

// Expand a clear-set to include the effects of any special pieces caught in it.
function triggerSpecials(board, clear) {
  const processed = new Set();
  let changed = true;
  while (changed) {
    changed = false;
    for (const k of Array.from(clear)) {
      if (processed.has(k)) continue;
      processed.add(k);
      const r = Math.floor(k / COLS);
      const c = k % COLS;
      const cell = board[r][c];
      if (!cell) continue;
      if (cell.s === SPECIAL.ROW) {
        for (let cc = 0; cc < COLS; cc++) {
          const nk = key(r, cc);
          if (!clear.has(nk)) { clear.add(nk); changed = true; }
        }
      } else if (cell.s === SPECIAL.COL) {
        for (let rr = 0; rr < ROWS; rr++) {
          const nk = key(rr, c);
          if (!clear.has(nk)) { clear.add(nk); changed = true; }
        }
      } else if (cell.s === SPECIAL.CROSS) {
        for (let cc = 0; cc < COLS; cc++) {
          const nk = key(r, cc);
          if (!clear.has(nk)) { clear.add(nk); changed = true; }
        }
        for (let rr = 0; rr < ROWS; rr++) {
          const nk = key(rr, c);
          if (!clear.has(nk)) { clear.add(nk); changed = true; }
        }
      } else if (cell.s === SPECIAL.BOMB) {
        const bt = cell.t;
        for (let rr = 0; rr < ROWS; rr++)
          for (let cc = 0; cc < COLS; cc++) {
            if (board[rr][cc] && board[rr][cc].t === bt) {
              const nk = key(rr, cc);
              if (!clear.has(nk)) { clear.add(nk); changed = true; }
            }
          }
      }
    }
  }
}

// Which cells clear, and which become new specials, for the current matches.
// `preferred` biases where a new special spawns (the player's swapped cells).
function planClear(board, matches, preferred) {
  const clear = new Set();
  const hCells = new Set();
  const vCells = new Set();
  for (const m of matches) {
    const bucket = m.dir === "h" ? hCells : vCells;
    for (const cc of m.cells) {
      const k = key(cc.r, cc.c);
      clear.add(k);
      bucket.add(k);
    }
  }

  const specials = [];
  const used = new Set();

  // L/T/+ shapes: a cell in both a row-run and a column-run → CROSS.
  const intersections = new Set();
  for (const k of hCells) if (vCells.has(k)) intersections.add(k);
  for (const k of intersections) {
    const r = Math.floor(k / COLS);
    const c = k % COLS;
    specials.push({ r, c, s: SPECIAL.CROSS, t: board[r][c].t });
    used.add(k);
  }

  // Straight runs not part of an intersection → BOMB (≥5) or line blaster (==4).
  for (const m of matches) {
    if (m.len < 4) continue;
    if (m.cells.some((cc) => intersections.has(key(cc.r, cc.c)))) continue;
    let cell = m.cells.find((x) => !used.has(key(x.r, x.c)) && preferred.some((p) => p.r === x.r && p.c === x.c));
    if (!cell) cell = m.cells.find((x) => !used.has(key(x.r, x.c)));
    if (!cell) continue;
    const s = m.len >= 5 ? SPECIAL.BOMB : m.dir === "h" ? SPECIAL.ROW : SPECIAL.COL;
    specials.push({ r: cell.r, c: cell.c, s, t: board[cell.r][cell.c].t });
    used.add(key(cell.r, cell.c));
  }

  for (const sp of specials) clear.delete(key(sp.r, sp.c)); // specials survive
  return { clear, specials };
}

function applyClear(board, clear) {
  for (const k of clear) board[Math.floor(k / COLS)][k % COLS] = null;
}

function dropAndRefill(board, types, rng) {
  for (let c = 0; c < COLS; c++) {
    let write = ROWS - 1;
    for (let r = ROWS - 1; r >= 0; r--) {
      if (board[r][c] !== null) {
        if (write !== r) { board[write][c] = board[r][c]; board[r][c] = null; }
        write--;
      }
    }
    for (let r = write; r >= 0; r--) board[r][c] = { t: randType(rng, types), s: SPECIAL.NONE };
  }
}

function applyClearAndGravity(board, clear, specials, types, rng) {
  applyClear(board, clear); // clear first…
  for (const sp of specials) board[sp.r][sp.c] = { t: sp.t, s: sp.s }; // …then place new specials so they survive
  dropAndRefill(board, types, rng);
}

// Detonate two specials swapped together. Returns the cells to clear + a bonus
// flag (double-bomb). Combos:
//   line + line → 3 rows and 3 columns centred on the swap
//   bomb + line → every tile of the line's colour blasts its row AND column
//   bomb + bomb → the entire board (super bonus)
function detonateCombo(board, a, b, sa, sb) {
  const clear = new Set();
  clear.add(key(a.r, a.c));
  clear.add(key(b.r, b.c));

  if (sa === SPECIAL.BOMB && sb === SPECIAL.BOMB) {
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) clear.add(key(r, c));
    return { clear, bonus: true };
  }

  if (sa === SPECIAL.BOMB || sb === SPECIAL.BOMB) {
    const linePos = sa === SPECIAL.BOMB ? b : a;
    const color = board[linePos.r][linePos.c].t;
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++) {
        if (board[r][c] && board[r][c].t === color) {
          for (let cc = 0; cc < COLS; cc++) clear.add(key(r, cc));
          for (let rr = 0; rr < ROWS; rr++) clear.add(key(rr, c));
        }
      }
    return { clear, bonus: false };
  }

  // line + line
  for (let dr = -1; dr <= 1; dr++) {
    const rr = a.r + dr;
    if (rr >= 0 && rr < ROWS) for (let cc = 0; cc < COLS; cc++) clear.add(key(rr, cc));
  }
  for (let dc = -1; dc <= 1; dc++) {
    const cc = a.c + dc;
    if (cc >= 0 && cc < COLS) for (let rr = 0; rr < ROWS; rr++) clear.add(key(rr, cc));
  }
  return { clear, bonus: false };
}

// Resolve one wave of matches. Returns cleared count, or done:true if none.
function resolveOnce(board, types, rng, preferred) {
  const matches = findMatches(board);
  if (!matches.length) return { done: true, cleared: 0, specials: 0 };
  const { clear, specials } = planClear(board, matches, preferred);
  triggerSpecials(board, clear);
  const cleared = clear.size;
  applyClearAndGravity(board, clear, specials, types, rng);
  return { done: false, cleared, specials: specials.length };
}

// Resolve matches + all cascades. Returns total points and stats. Points scale
// with the cascade depth so chain reactions are rewarding.
export function resolveBoard(board, types, rng = Math.random, preferred = []) {
  let points = 0;
  let cascades = 0;
  let specials = 0;
  let first = true;
  for (let i = 0; i < 100; i++) {
    const res = resolveOnce(board, types, rng, first ? preferred : []);
    first = false;
    if (res.done) break;
    cascades++;
    points += res.cleared * 10 * cascades;
    specials += res.specials;
  }
  if (!hasMove(board, types)) reshuffle(board, types, rng);
  return { points, cascades, specials };
}

function swapCells(board, a, b) {
  const tmp = board[a.r][a.c];
  board[a.r][a.c] = board[b.r][b.c];
  board[b.r][b.c] = tmp;
}

// Attempt a swap. On success mutates `board` (resolving matches + cascades) and
// returns { ok:true, points, cascades, specials, combo? }. On an illegal swap
// returns { ok:false } and leaves the board unchanged.
export function trySwap(board, a, b, types, rng = Math.random) {
  if (!areAdjacent(a, b)) return { ok: false };
  swapCells(board, a, b);
  const sa = board[a.r][a.c].s;
  const sb = board[b.r][b.c].s;

  // ── Combo: two specials swapped together ────────────────────────────────
  if (sa !== SPECIAL.NONE && sb !== SPECIAL.NONE) {
    const { clear, bonus } = detonateCombo(board, a, b, sa, sb);
    triggerSpecials(board, clear); // chain any other specials caught in the blast
    const cleared = clear.size;
    applyClear(board, clear);
    dropAndRefill(board, types, rng);
    const rest = resolveBoard(board, types, rng, []);
    if (!hasMove(board, types)) reshuffle(board, types, rng);
    return { ok: true, points: cleared * 20 + (bonus ? 3000 : 0) + rest.points, cascades: 1 + rest.cascades, specials: rest.specials, combo: true };
  }

  // ── Single special swapped → activate it ────────────────────────────────
  if (sa !== SPECIAL.NONE || sb !== SPECIAL.NONE) {
    const sPos = sa !== SPECIAL.NONE ? a : b;
    const oPos = sa !== SPECIAL.NONE ? b : a;
    const s = board[sPos.r][sPos.c].s;
    const clear = new Set([key(sPos.r, sPos.c)]);
    if (s === SPECIAL.ROW) {
      for (let cc = 0; cc < COLS; cc++) clear.add(key(sPos.r, cc));
    } else if (s === SPECIAL.COL) {
      for (let rr = 0; rr < ROWS; rr++) clear.add(key(rr, sPos.c));
    } else if (s === SPECIAL.BOMB) {
      const color = board[oPos.r][oPos.c].t; // colour bomb clears the colour it's swapped with
      for (let rr = 0; rr < ROWS; rr++)
        for (let cc = 0; cc < COLS; cc++)
          if (board[rr][cc] && board[rr][cc].t === color) clear.add(key(rr, cc));
      clear.add(key(oPos.r, oPos.c));
    }
    triggerSpecials(board, clear);
    const cleared = clear.size;
    applyClear(board, clear);
    dropAndRefill(board, types, rng);
    const rest = resolveBoard(board, types, rng, []);
    if (!hasMove(board, types)) reshuffle(board, types, rng);
    return { ok: true, points: cleared * 12 + rest.points, cascades: 1 + rest.cascades, specials: rest.specials };
  }

  // ── Both normal → require a real match ──────────────────────────────────
  if (findMatches(board).length === 0) {
    swapCells(board, a, b); // revert
    return { ok: false };
  }
  const res = resolveBoard(board, types, rng, [a, b]);
  return { ok: true, ...res };
}

// Is there any legal move (a swap that makes a match, or any special present)?
export function hasMove(board, types = MAX_TYPES) {
  for (let r = 0; r < ROWS; r++)
    for (let c = 0; c < COLS; c++) {
      if (board[r][c] && board[r][c].s !== SPECIAL.NONE) return true;
      for (const [dr, dc] of [[0, 1], [1, 0]]) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr >= ROWS || nc >= COLS) continue;
        swapCells(board, { r, c }, { r: nr, c: nc });
        const ok = findMatches(board).length > 0;
        swapCells(board, { r, c }, { r: nr, c: nc });
        if (ok) return true;
      }
    }
  return false;
}

// Shuffle pieces in place until at least one move exists and no free matches.
export function reshuffle(board, types, rng = Math.random) {
  const flat = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) flat.push(board[r][c]);
  for (let attempt = 0; attempt < 60; attempt++) {
    for (let i = flat.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [flat[i], flat[j]] = [flat[j], flat[i]];
    }
    let idx = 0;
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) board[r][c] = flat[idx++];
    if (findMatches(board).length === 0 && hasMove(board, types)) return;
  }
}
